import type React from 'react';
import { useCallback, useEffect, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { useTranslate } from 'react-admin';

import { useIngordDataProvider } from 'src/components/flowchart/hooks/useIngordDataProvider';

import type { SelectChangeEvent } from '@mui/material';

interface DefaultTemplateDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (markup: string) => void;
}

export const DefaultTemplateDialog = ({ open, onClose, onConfirm }: DefaultTemplateDialogProps): React.ReactElement => {
  const translate = useTranslate();
  const dataProvider = useIngordDataProvider();
  const [defaults, setDefaults] = useState<Record<string, string>>({});
  const [selected, setSelected] = useState('');

  useEffect(() => {
    if (!open) return;
    void dataProvider.getApTemplateDefaults().then((data) => {
      setDefaults(data);
      setSelected('');
    });
  }, [open, dataProvider]);

  const handleConfirm = useCallback(() => {
    if (!selected) return;
    onConfirm(defaults[selected] ?? '');
    onClose();
  }, [selected, defaults, onConfirm, onClose]);

  const keys = Object.keys(defaults);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='sm'>
      <DialogTitle component='div'>
        <Typography variant='h6'>{translate('resources.ap_templates.defaultTemplate.dialogTitle')}</Typography>
      </DialogTitle>
      <DialogContent>
        <Box mt={2} display='flex' flexDirection='column' gap={2}>
          <Alert severity='warning'>{translate('resources.ap_templates.defaultTemplate.warning')}</Alert>
          <FormControl fullWidth>
            <InputLabel>{translate('resources.ap_templates.defaultTemplate.selectLabel')}</InputLabel>
            <Select
              value={selected}
              label={translate('resources.ap_templates.defaultTemplate.selectLabel')}
              onChange={(e: SelectChangeEvent) => {
                setSelected(e.target.value);
              }}
            >
              {keys.map((key) => (
                <MenuItem key={key} value={key}>
                  {key}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{translate('ra.action.cancel')}</Button>
        <Button variant='contained' onClick={handleConfirm} disabled={!selected}>
          {translate('ra.action.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
